import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule, KeyValue } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  NgModule,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormGroupDirective,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { IncludesPipeModule } from '@commons/pipes/includes/includes.pipe';
import { NgUpperFirstPipeModule } from '@wizbii/angular-pipes';
import { WINDOW } from '@wizbii/angular-utilities';
import { BehaviorSubject } from 'rxjs';

const SELECT_EXTRA_WIDTH = 50;
const MORE_BOX_WIDTH = 5;
const MIN_REQUIRED_WIDTH = 100;

interface DropdownValue {
  label: string;
  value: string;
}

@Component({
  selector: 'app-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DropdownComponent implements ControlValueAccessor, OnInit, OnDestroy, Validator, AfterViewInit {
  @Input() label!: string;
  @Input() ariaLabel!: string;
  @Input() multiple!: boolean;
  @Input() maxChoice!: number;
  @Input() options!: Record<string, string>;
  @Input() doubleColumn!: boolean;
  @Input() liveSearch!: boolean;
  @Input() advancedFilterSearchEngine!: boolean;
  @Input() orderByAscd!: boolean;
  @Output() panelTriggered = new EventEmitter<boolean>();
  @Output() valueUpdated = new EventEmitter<boolean>();

  set value(value: DropdownValue[]) {
    if (value?.length === 0) {
      this._control.setValue(undefined);
      return;
    }

    if (this._value === value) {
      return;
    }
    this._value = value;
    this._control.setValue(value);
    this.onChanged(value);
    this.onTouched();
  }

  get value(): DropdownValue[] {
    return this._value;
  }

  @Input()
  set disabled(disabled: boolean) {
    this.setDisabledState(disabled);
  }

  private _ieColumn = 1;

  get ieColumn(): number {
    this._ieColumn++;

    if (this._ieColumn === 1) {
      return this._ieColumn;
    }

    this._ieColumn = 0;
    return 2;
  }

  private _ieRow = 1;
  get ieRow(): number {
    if (this._ieColumn % 2 === 0) {
      this._ieRow++;
    }

    return this._ieRow;
  }

  /**
   * Indicate the required state of the form control
   * @param {boolean} isRequired - The required state
   */
  @Input() set required(isRequired: boolean) {
    this._required = isRequired;

    if (this._required && this._control) {
      this._control.setValidators(Validators.required);
    }
  }

  get required(): boolean {
    return this._required;
  }

  _control: FormControl = new FormControl();
  _maxSelectedReached!: boolean;
  private _value!: DropdownValue[];
  private _disabled!: boolean;
  private _required!: boolean;

  elementHidden$ = new BehaviorSubject<string | undefined>(undefined);
  elementNameToReduce$ = new BehaviorSubject<string | undefined>(undefined);
  elementToReduceMaxWidth$ = new BehaviorSubject<number | undefined>(undefined);
  parentFormSubmitted$ = new BehaviorSubject<boolean>(false);

  typePanelClass$ = new BehaviorSubject<string | undefined>(undefined);

  @ViewChild('inputField') inputField!: ElementRef;
  @ViewChildren('inputValues') inputValues!: QueryList<ElementRef>;
  @ViewChild('select') select!: MatSelect;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly renderer: Renderer2,
    private readonly controlContainer: ControlContainer,
    @Inject(WINDOW) private readonly window: any
  ) {}

  validate(): ValidationErrors | null {
    return this._control.errors;
  }

  ngOnInit(): void {
    if (this.controlContainer?.formDirective) {
      (this.controlContainer.formDirective as FormGroupDirective).ngSubmit.subscribe({
        next: () => {
          this.parentFormSubmitted$.next(true);
        },
      });
    }

    this._control.valueChanges.subscribe({
      next: (value) => {
        this.value = value;
        this.valueUpdated.emit();
      },
    });

    this.typePanelClass$.next(
      this.doubleColumn
        ? 'dropdown-panel dropdown-panel-search dropdown-panel-double-column'
        : 'dropdown-panel dropdown-panel-search'
    );
  }

  ngAfterViewInit(): void {
    this.window.setTimeout(() => {
      this.selectionChanged();
    });
  }

  ascendantOrder = (a: KeyValue<string, string>, b: KeyValue<string, string>): number => {
    if (this.orderByAscd) {
      return a.value.localeCompare(b.value);
    }

    return 0;
  };

  ngOnDestroy(): void {
    this.elementHidden$.complete();
    this.elementNameToReduce$.complete();
    this.elementToReduceMaxWidth$.complete();
    this.parentFormSubmitted$.complete();
    this.typePanelClass$.complete();
  }

  /**
   * Handle the display of the selected value
   * If the selected values length is wider than the input field, then the last displayable element
   * get its width adapted and the right ones are hidden (in this case, display how many are hidden)
   * If the last displayable element width can not be adapted with a sufficient minimal width, then simply hide it
   */
  // tslint:disable:cognitive-complexity
  selectionChanged(): void {
    this._maxSelectedReached = !!this.maxChoice && this._control.value.length >= this.maxChoice;

    this.writeValue(this._control.value);
    if (this.multiple) {
      this.handleSelectionResize();
    }
    this.cdr.detectChanges();
  }

  private handleSelectionResize(): void {
    const maxContainerSize = this.select._elementRef.nativeElement.offsetWidth - SELECT_EXTRA_WIDTH;

    this.elementNameToReduce$.next(undefined);
    this.elementHidden$.next(undefined);
    this.cdr.detectChanges();
    let sizeChildren = 0;

    const classHide = 'hide-input';
    let enoughDisplayed = false;

    const inputValuesArray = this.inputValues.toArray();

    for (let i = 0; i < inputValuesArray.length; i++) {
      const element = inputValuesArray[i];
      this.renderer.removeClass(element.nativeElement, classHide);
      sizeChildren += parseInt(element.nativeElement.offsetWidth, 10);

      if (enoughDisplayed) {
        this.renderer.addClass(inputValuesArray[i].nativeElement, classHide);
      } else if (sizeChildren > maxContainerSize) {
        enoughDisplayed = true;

        const maxDispo = Math.abs(maxContainerSize - sizeChildren + parseInt(element.nativeElement.offsetWidth, 10));
        this.elementNameToReduce$.next(element.nativeElement.textContent.trim());

        const isTooSmall = maxDispo < MIN_REQUIRED_WIDTH;
        this.elementToReduceMaxWidth$.next(isTooSmall ? 0 : maxDispo - MORE_BOX_WIDTH);

        this.reduceMaxWidth(isTooSmall, element, classHide, maxDispo);

        const remainingItemsCount = inputValuesArray.length - i - 1 + (isTooSmall ? 1 : 0);

        this.updateRemainingItems(remainingItemsCount);
      }
    }
  }

  private reduceMaxWidth(isTooSmall: boolean, element: ElementRef<any>, classHide: string, maxDispo: number): void {
    if (isTooSmall) {
      this.elementToReduceMaxWidth$.next(0);
      this.renderer.addClass(element.nativeElement, classHide);
    } else {
      const maxWidth = maxDispo - MORE_BOX_WIDTH;
      this.elementToReduceMaxWidth$.next(maxWidth);
    }
  }

  private updateRemainingItems(remainingItemsCount: number): void {
    if (remainingItemsCount > 0) {
      this.elementHidden$.next(`+${remainingItemsCount}`);
    } else {
      this.elementHidden$.next(undefined);
    }
  }

  registerOnChange(fn: (value: DropdownValue[]) => void): void {
    this.onChanged = (value) => {
      fn(value);
      this.cdr.markForCheck();
    };
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = () => {
      fn();
      this.cdr.markForCheck();
    };
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = coerceBooleanProperty(isDisabled);

    if (!this._control) {
      return;
    }

    if (this._disabled) {
      this._control.disable();
    } else {
      this._control.enable();
    }
  }

  trackByIndex(index: number, _: any): number {
    return index;
  }

  writeValue = (value: DropdownValue[]): DropdownValue[] => (this.value = value);

  clearValue(event: any): void {
    if (event) {
      event.stopPropagation();
    }

    this._control.setValue(undefined);
  }

  onChanged: (_: DropdownValue[]) => void = (_: DropdownValue[]) => {
    // intentional empty arrow function
  };
  onTouched: () => void = () => {
    // intentional empty arrow function
  };

  trackByOption(_: number, option: KeyValue<string, string>): string {
    return option.key;
  }
}

@NgModule({
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    NgUpperFirstPipeModule,
    IncludesPipeModule,
  ],
  exports: [DropdownComponent],
  declarations: [DropdownComponent],
})
export class DropdownModule {}
